import type { ICellRendererParams } from 'ag-grid-enterprise';
import { get, isFunction } from 'lodash-es';
import type { PropsWithChildren, ReactNode } from 'react';
import { useCurrenciesContext, useSecuritiesContext } from '../../contexts';
import { Order } from '../../types/Order';
import { type ExpressedOutcomeEnum, OrdTypeEnum, SideEnum } from '../../types/types';
import {
  getPricingReference,
  messages as prettyPricingMessages,
  prettyPricingMode,
  toBigWithDefault,
} from '../../utils';
import type { PriceValue } from '../BlotterTable/aggFuncs/priceAggFunc';
import { Box, Flex } from '../Core';
import { InlineFormattedNumber, NumberVariants } from '../FormattedNumber';
import { IconName } from '../Icons';
import { IndicatorBadge } from '../IndicatorBadge';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import type { AgGridPriceProps } from './AgGridPrice.types';

function isPrettyPricingMessageKey(key: string): key is keyof typeof prettyPricingMessages {
  return key in prettyPricingMessages;
}

export function AgGridPrice({
  value,
  data,
  showCurrency = true,
  round,
  increment: incrementInput,
  useQuoteCurrencyIncrement = false,
  showFeeIcon = 'auto',
  colDef,
  side: inputSide,
  getSide,
  showReferencePrice,
  showDashIfZero,
  expressedOutcomeField,
  transformPriceForDisplay,
}: AgGridPriceProps & Pick<ICellRendererParams<unknown, PriceValue>, 'colDef' | 'value' | 'data'>) {
  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const securityInfo = value?.asset ? securitiesBySymbol.get(value.asset) : undefined;
  const currencyInfo = value?.quoteCurrency ? currenciesBySymbol.get(value.quoteCurrency) : undefined;
  const ordType = data instanceof Order ? data.OrdType : undefined;
  const expressedOutcome: ExpressedOutcomeEnum | undefined = expressedOutcomeField
    ? get(data, expressedOutcomeField)
    : undefined;

  let shouldShowFeeIcon = showFeeIcon === 'always';
  if (showFeeIcon === 'auto') {
    shouldShowFeeIcon = ordType === OrdTypeEnum.LimitAllIn || colDef?.field === 'AvgPxAllIn';
  }

  // If a sideField is provided, we try to derive the color (variant) from the resolved side
  const side: unknown = inputSide ?? getSide?.(data);
  const variant =
    side === SideEnum.Buy ? NumberVariants.Positive : side === SideEnum.Sell ? NumberVariants.Negative : undefined;

  const maybePricingMode = get(data, 'PricingMode');
  const maybePricingReference = get(data, 'PricingReference');

  if (!value || !value.price) {
    if (showReferencePrice) {
      // If no price returned from market (bad connection or order not yet started), we show the reference price
      const amount = getPricingReference({ PricingMode: maybePricingMode, PricingReference: maybePricingReference });
      return (
        <InlineFormattedNumber number={amount?.value} currency={amount?.currency} round={round} variant={variant} />
      );
    }

    return <></>;
  }
  if (showDashIfZero && toBigWithDefault(value.price, 0).eq(0)) {
    return '-';
  }

  const increment = isFunction(incrementInput) ? incrementInput(data) : incrementInput;
  const shouldShowCurrency = isFunction(showCurrency) ? showCurrency(data) : showCurrency;
  const defaultIncrement = useQuoteCurrencyIncrement
    ? (currencyInfo?.DefaultIncrement ?? securityInfo?.DefaultPriceIncrement)
    : (securityInfo?.DefaultPriceIncrement ?? currencyInfo?.DefaultIncrement);
  const displayPrice = transformPriceForDisplay ? transformPriceForDisplay(value.price, data) : value.price;
  const displayText = typeof displayPrice === 'string' ? displayPrice : undefined;
  const quoteCurrency = currencyInfo?.Symbol ?? value.quoteCurrency;

  return (
    <ReferencePriceTooltip
      disabled={!showReferencePrice}
      entity={{ PricingMode: maybePricingMode, PricingReference: maybePricingReference }}
    >
      <BestPriceBox isBestPrice={value.isBestPrice}>
        <InlineFormattedNumber
          round={round}
          number={displayText === undefined ? displayPrice : value.price}
          displayText={displayText}
          variant={variant}
          increment={increment ?? defaultIncrement}
          currency={shouldShowCurrency && value.endPrice === undefined ? quoteCurrency : null}
          startIcon={shouldShowFeeIcon ? IconName.Fees : undefined}
        />

        {value.endPrice && (
          <>
            &nbsp;-&nbsp;
            <InlineFormattedNumber
              round={round}
              number={value.endPrice}
              variant={variant}
              increment={increment ?? defaultIncrement}
              currency={shouldShowCurrency ? quoteCurrency : null}
            />
          </>
        )}
      </BestPriceBox>

      {expressedOutcome && <ExpressedOutcomeIndicator expressedOutcome={expressedOutcome} />}
    </ReferencePriceTooltip>
  );
}

function BestPriceBox({ isBestPrice, children }: PropsWithChildren<{ isBestPrice: boolean }>) {
  if (!isBestPrice) {
    return children;
  }

  return (
    <Box p="spacing4" borderRadius="2px" background="colors.primary.dim" data-best-price>
      {children}
    </Box>
  );
}

function ExpressedOutcomeIndicator({ expressedOutcome }: { expressedOutcome: ExpressedOutcomeEnum }) {
  return (
    <IndicatorBadge variant={expressedOutcome === 'Yes' ? 'POSITIVE' : 'NEGATIVE'} ml="spacing4">
      {expressedOutcome === 'Yes' ? 'Y' : 'N'}
    </IndicatorBadge>
  );
}

/**
 * Renders the PricingReference as a tooltip.
 */
export function ReferencePriceTooltip({
  entity,
  disabled = false,
  children,
}: {
  entity: Partial<Pick<Order, 'PricingReference' | 'PricingMode'>>;
  disabled?: boolean;
  children: ReactNode;
}) {
  if (!entity.PricingMode || !entity.PricingReference || disabled) {
    return <>{children}</>;
  }

  const amount = getPricingReference(entity);
  if (!amount) {
    return <>{children}</>;
  }

  const pricingMode = prettyPricingMode(entity.PricingMode);
  const renderedPricingMode = isPrettyPricingMessageKey(pricingMode) ? (
    <FormattedMessage {...prettyPricingMessages[pricingMode]} />
  ) : (
    pricingMode
  );

  return (
    <Tooltip
      usePortal
      showUnderline
      tooltip={
        <Flex gap="spacing4">
          <Text>{renderedPricingMode}:</Text>
          <InlineFormattedNumber number={amount?.value} currency={amount?.currency} />
        </Flex>
      }
    >
      {children}
    </Tooltip>
  );
}
